import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "interactive-visualization-systems",
  kind: "horizontal",
  title: "Interactive 2D & 3D Visualization Systems",
  shortTitle: "2D/3D Visualization",
  status: "draft",
  tier: "we-do",
  seoTitle: "Interactive 2D & 3D Visualization Systems | GA",
  seoDescription:
    "Interactive 2D/3D equipment models, P&IDs, schematics, and part drill-down interfaces rendered in standard web browsers without plugins.",
  summary:
    "Interactive equipment models, schematics, and network diagrams that run in a browser, with part-level drill-down, live telemetry overlays, and metadata attached directly to what the user clicks.",
  image: "/images/blog/evaluating-2d-and-3d-web-graphics-hero.webp",
  relatedCapabilitySlugs: [
    "data-search-knowledge",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: ["military", "energy", "media-entertainment"],
  relatedPostSlugs: [
    "s1000d-geometry-gap",
    "dashboard-design",
    "evaluating-2d-and-3d-web-graphics",
    "why-naive-browser-cad-fails",
    "interactive-industrial-schematics",
    "bridging-3d-meshes-to-part-catalogs",
    "high-frequency-telemetry-rendering",
    "interactive-wiring-and-pids",
  ],
  engineering: [
    "Interactive 2D/3D equipment model viewers with hierarchical part drill-down",
    "Hotspot-bound illustrated parts catalogs (IPCs) and vector schematics (SVG/Canvas)",
    "Web-based Piping & Instrumentation Diagrams (P&IDs) and controls block schematics",
    "Live telemetry overlays, state coloring, and high-frequency instrumentation gauges",
    "Spatial coordinate and scene-graph binding to relational asset and parts databases",
    "CAD-to-web geometry optimization: mesh decimation, LOD generation, and glTF pipelines",
    "Multi-display video wall consoles and reconfigurable operator dashboard layouts",
    "Hardware-constrained WebGL/WebGPU rendering and memory-managed context lifecycles",
  ],
  phases: {
    study: {
      focus:
        "Audit source CAD, vector drawings, and telemetry streams to define geometry budgets and performance limits.",
      produces: [
        "CAD and schematic format audit (STEP, JT, glTF, DXF, SVG, Visio)",
        "Polygon, draw-call, and client memory budget specifications",
        "Telemetry payload and update frequency ingestion analysis",
        "Device hardware tiering and browser runtime profiling",
      ],
      fit: {
        good: [
          "Industrial or defense operators needing field equipment inspection on standard tablets and ruggedized laptops",
          "Operations centers looking to replace static paper P&IDs or Visio diagrams with interactive, data-linked schematics",
          "Equipment manufacturers requiring web-based parts catalogs with discrete sub-assembly inspection",
        ],
        poor: [
          "Consumer e-commerce sites needing simple 360-degree photo spinners",
          "Parametric CAD authoring tools intended for mechanical design engineering rather than operational visualization",
        ],
      },
    },
    "initial-design": {
      focus:
        "Establish the scene graph hierarchy, 2D/3D coordinate binding contracts, and interaction architecture.",
      produces: [
        "Hierarchical scene graph and part tagging taxonomy",
        "2D schematic to 3D spatial coordinate mapping specification",
        "Real-time telemetry ingestion contract (WebSockets/SSE)",
        "Camera navigation, clipping, and raycast picking architecture",
      ],
    },
    "detailed-design": {
      focus:
        "Build the automated geometry processing pipeline, custom shader/canvas renderers, and UI overlays.",
      produces: [
        "Automated mesh simplification and Level-of-Detail (LOD) pipeline",
        "Hotspot callout coordinate mapper and vector annotation engine",
        "Real-time canvas instrumentation and telemetry gauge renderers",
        "Sub-assembly search and relational metadata lookup integration",
      ],
    },
    implementation: {
      focus:
        "Deploy production-grade, memory-leak-free interactive viewers across desktop consoles and field hardware.",
      produces: [
        "Browser-based 2D/3D visualization application with part drill-downs",
        "Context-loss-resilient WebGL/WebGPU rendering pipeline",
        "Offline asset bundles for disconnected field operations",
        "Multi-monitor video wall dashboard configurations",
      ],
    },
  },
};
